import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Container,
    TextField,
    Button,
    Alert,
    CircularProgress
} from '@mui/material';
import axios from 'axios';
import { workshopDomain, phonepeDomain } from '../util/domain';

const OtpVerification = () => {
    const { registrationHashId } = useParams();
    const navigate = useNavigate();
    const [studentData, setStudentData] = useState(null);
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState('show'); // show, otp, verifying
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [editMobile, setEditMobile] = useState(false);
    const [newMobile, setNewMobile] = useState('');

    useEffect(() => {
        // 1. Fetch registration details
        const fetchRegistrationDetails = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get(`${workshopDomain}/api/registration/${registrationHashId}`);
                if (response.data.success) {
                    setStudentData(response.data.registration);
                    setMobile(response.data.registration.mobile || '');
                    // If mobile is already verified, redirect accordingly
                    const reg = response.data.registration;
                    if (reg.mobile_verified === true || reg.mobile_verified === 1) {
                        if (reg.registration_fee > 0) {
                            window.location.href = `${phonepeDomain}/payment/${registrationHashId}`;
                        } else {
                            navigate(`/thankyou/${registrationHashId}`);
                        }
                        return;
                    }
                } else {
                    setError('Registration not found');
                }
            } catch (err) {
                setError('Failed to fetch registration details');
            } finally {
                setLoading(false);
            }
        };
        fetchRegistrationDetails();
    }, [registrationHashId, navigate]);

    // 3. Send OTP
    const handleSendOtp = async () => {
        if (!mobile || mobile.length !== 10) {
            setError('Please enter a valid 10-digit mobile number');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await axios.post(`${workshopDomain}/api/verification/send-otp`, {
                registrationHashId,
                mobile
            });
            if (response.data.success) {
                setOtpSent(true);
                setStep('otp');
                setSuccess('OTP sent successfully');
            } else {
                setError(response.data.message || 'Failed to send OTP');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    // 4. Verify OTP
    const handleVerifyOtp = async () => {
        if (!otp) {
            setError('Please enter the OTP');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await axios.post(`${workshopDomain}/api/verification/verify-otp`, {
                registrationHashId,
                studentHashId: studentData.student_hash_id,
                otp
            });
            if (response.data.success) {
                // 5. If payment required, redirect to payment, else thank you
                if (studentData && studentData.registration_fee > 0) {
                    window.location.href = `${phonepeDomain}/payment/${registrationHashId}`;
                } else {
                    navigate(`/thankyou/${registrationHashId}`);
                }
            } else {
                setError(response.data.message || 'Invalid OTP');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'OTP verification failed');
        } finally {
            setLoading(false);
        }
    };

    // Save new mobile number
    const handleSaveMobile = async () => {
        if (!newMobile || newMobile.length !== 10) {
            setError('Please enter a valid 10-digit mobile number');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await axios.post(`${workshopDomain}/api/registration/update-mobile`, {
                registrationHashId,
                studentHashId: studentData.student_hash_id,
                mobile: newMobile
            });
            if (response.data.success) {
                setMobile(newMobile);
                setEditMobile(false);
                setSuccess('Mobile number updated successfully');
            } else {
                setError(response.data.message || 'Failed to update mobile number');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update mobile number');
        } finally {
            setLoading(false);
        }
    };

    if (loading && !studentData) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm">
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                <img src="/logo-vector.svg" alt="Vector India Logo" style={{ height: '60px', width: 'auto' }} />
            </Box>
            <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    OTP Verification
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                {studentData && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" gutterBottom>
                            Please verify your mobile number to continue.
                        </Typography>
                        <Box sx={{ my: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Name:</strong> {studentData.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Mobile:</strong> {editMobile ? (
                                    <>
                                        <input
                                            type="text"
                                            value={newMobile}
                                            onChange={e => setNewMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                            maxLength={10}
                                            style={{ marginRight: 8 }}
                                        />
                                        <Button size="small" variant="contained" onClick={handleSaveMobile} disabled={loading}>
                                            Save
                                        </Button>
                                        <Button size="small" variant="text" onClick={() => setEditMobile(false)} disabled={loading}>
                                            Cancel
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        {mobile}
                                        <Button size="small" variant="text" onClick={() => { setEditMobile(true); setNewMobile(mobile); }} style={{ marginLeft: 8 }}>
                                            Edit
                                        </Button>
                                    </>
                                )}
                            </Typography>
                        </Box>
                        {step === 'show' && (
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleSendOtp}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Send OTP to Mobile'}
                            </Button>
                        )}
                        {step === 'otp' && (
                            <>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="otp"
                                    label="Enter OTP"
                                    name="otp"
                                    value={otp}
                                    onChange={e => setOtp(e.target.value)}
                                    inputProps={{ maxLength: 6 }}
                                    sx={{ mt: 2 }}
                                />
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleVerifyOtp}
                                    disabled={loading}
                                    sx={{ mt: 2 }}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Verify OTP'}
                                </Button>
                            </>
                        )}
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default OtpVerification; 